/**
 * A CLOCK THAT HOLDS STILL LONG ENOUGH TO BE A MEMO KEY.
 *
 * WHAT BROKE. Five useMemos across four screens wrapped a workspace-wide
 * roll-up — every card on every board — and then listed `now` among their
 * dependencies, where `now` was `Date.now()` computed in the render body:
 *
 *     MyWork.tsx     myWork over every card, and rollUpWorkspaceEpics
 *     EpicsLens.tsx  rollUpWorkspaceEpics, and the board filter cascading off it
 *     AreasLens.tsx  rollUpWorkspaceAreas over every board's cards
 *     EpicDetail.tsx rollUpEpic over the epic's members
 *
 * A dependency that is new on every render is not a dependency: the key never
 * matched, so not one of those memos ever cached, and every render recomputed
 * the lot. The results were always correct — the roll-ups are pure — so nothing
 * looked wrong. It was simply all done again, every time, on the screen set as
 * the default landing view.
 *
 * WHY IT MATTERED MORE THAN IT LOOKS. My Work and the Portfolio hold a live
 * cross-board listener for as long as they are open, so every change anywhere
 * re-renders them — and every one of those re-renders re-ran every roll-up.
 * The read side of that was measured separately as the board-load fan-out; this
 * was the same multiplier on the client.
 *
 * WHAT THIS DOES, AND DELIBERATELY DOES NOT DO. It floors the clock to the
 * minute, so repeated calls inside one render — and across every render in that
 * minute — return the identical number, which is all a memo key needs.
 *
 * THERE IS NO TIMER HERE, ON PURPOSE. A ticking hook would keep the displayed
 * clock advancing on its own, and would do it by re-rendering four screens once
 * a minute, forever — adding renders to fix a bug whose whole point is wasted
 * renders. Nothing advanced the clock before this change either: a render
 * happened when something else caused one, and read the time then. That
 * behaviour is preserved exactly. The only thing removed is the instability.
 *
 * WHY A MINUTE, AND NOT AN HOUR. Everything downstream is day-resolution, and
 * the day it means is the reader's LOCAL day, not the UTC one:
 *
 *     dueDaysOf   my-work.ts   local Y/M/D, packed into a UTC frame to subtract
 *     daysUntil   EpicDetail.tsx:56   the same again, file-local
 *     cardDueState phases.ts -> daysPast   local midnight outright
 *
 * The UTC calls in the first two are only a stable frame for the subtraction;
 * the boundary each one straddles is local midnight. That is the boundary this
 * floor must never carry a render across, and it is why the step is a MINUTE.
 * Local midnight falls on a whole minute in every real offset — including the
 * half- and three-quarter-hour ones, +5:30, +5:45, +12:45, -3:30, +8:45 — so a
 * floor of under 60s cannot cross it. An HOUR floor would: in +5:30 it lands
 * 30 minutes the wrong side of local midnight and shifts the day by one.
 * A minute is therefore both finer than any consumer can read and coarse
 * enough that a burst of renders shares one value.
 */

/** The granularity the clock is held to. Finer than any consumer reads. */
export const STEADY_STEP_MS = 60_000;

/**
 * The time, held still within the step — safe to use as a memo dependency.
 *
 * `at` exists so this is testable without waiting on the real clock; call it
 * with no arguments everywhere else.
 */
export function steadyNow(at: number = Date.now(), stepMs: number = STEADY_STEP_MS): number {
  return Math.floor(at / stepMs) * stepMs;
}
